import React, { useState, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { Globe, CheckCircle, AlertCircle } from 'lucide-react';
import './styles.css';

export default function LanguageDetectionNode({ data, id }) {
  const [isEditing, setIsEditing] = useState(false);
  const [detectionMethod, setDetectionMethod] = useState(data?.detectionMethod || 'auto');
  const [languages, setLanguages] = useState(data?.languages || ['en', 'es', 'fr']);
  const [customMessage, setCustomMessage] = useState(data?.customMessage || 'Which language do you prefer?');
  const [timeoutSeconds, setTimeoutSeconds] = useState(data?.timeoutSeconds || 30);
  const { setNodes, getNodes } = useReactFlow();

  // Check which AI nodes have matching language variants
  const missingLanguageVariants = useMemo(() => {
    const allNodes = getNodes() || [];
    const aiNodes = allNodes.filter(n => n.type === 'ai');
    const languagesWithNodes = aiNodes
      .filter(n => n.data && n.data.language)
      .map(n => n.data.language);

    return languages.filter(lang => !languagesWithNodes.includes(lang));
  }, [languages, getNodes]);

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                detectionMethod,
                languages,
                customMessage,
                timeoutSeconds,
              },
            }
          : node
      )
    );
    setIsEditing(false);
  };

  const LANGUAGE_OPTIONS = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ko', name: 'Korean' },
    { code: 'hi', name: 'Hindi' },
    { code: 'nl', name: 'Dutch' },
    { code: 'tr', name: 'Turkish' },
    { code: 'pl', name: 'Polish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'ru', name: 'Russian' },
  ];

  const isValidConfig = languages.length >= 2 && customMessage.trim() !== '';
  const hasMissingVariants = missingLanguageVariants.length > 0;

  const statusBadge = !isValidConfig ? (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
      ⚠️ Missing Config
    </span>
  ) : hasMissingVariants ? (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full">
      <AlertCircle className="w-3 h-3" /> Missing Variants
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
      <CheckCircle className="w-3 h-3" /> Ready
    </span>
  );

  if (isEditing) {
    return (
      <div className="language-detection-edit">
        <div className="edit-header">
          <h3>🌐 Language Detection</h3>
          {statusBadge}
        </div>

        <div className="edit-body">
          <div className="form-group">
            <label>Detection Method *</label>
            <select
              value={detectionMethod}
              onChange={(e) => setDetectionMethod(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="auto">Auto-detect from speech</option>
              <option value="ask">Ask user to confirm/select</option>
              <option value="both">Detect first, then confirm</option>
            </select>
            <small>How to determine the user's language preference</small>
          </div>

          <div className="form-group">
            <label>Supported Languages *</label>
            <div className="language-checkboxes">
              {LANGUAGE_OPTIONS.map((lang) => (
                <label key={lang.code} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={languages.includes(lang.code)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setLanguages([...languages, lang.code]);
                      } else {
                        setLanguages(languages.filter((l) => l !== lang.code));
                      }
                    }}
                  />
                  {lang.name} ({lang.code})
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Prompt Message *</label>
            <input
              type="text"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="e.g., Which language do you prefer?"
              className="w-full px-3 py-2 border rounded"
            />
            <small>Message played to ask for language confirmation</small>
          </div>

          <div className="form-group">
            <label>Timeout (seconds)</label>
            <input
              type="number"
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(parseInt(e.target.value))}
              min="5"
              max="60"
              className="w-full px-3 py-2 border rounded"
            />
            <small>How long to wait for user response before continuing</small>
          </div>

          {languages.length < 2 && (
            <div className="form-group" style={{ padding: '8px 12px', backgroundColor: '#fee2e2', borderRadius: '4px', borderLeft: '3px solid #dc2626' }}>
              <small style={{ color: '#7f1d1d' }}>
                ⚠️ Language detection requires at least 2 languages
              </small>
            </div>
          )}

          {hasMissingVariants && (
            <div className="form-group" style={{ padding: '8px 12px', backgroundColor: '#fef3c7', borderRadius: '4px', borderLeft: '3px solid #d97706' }}>
              <small style={{ color: '#92400e' }}>
                ⚠️ Missing AI nodes for: <strong>{missingLanguageVariants.join(', ')}</strong><br/>
                Please create AI nodes with language codes: {missingLanguageVariants.map(lang => `<code>${lang}</code>`).join(', ')}
              </small>
            </div>
          )}

          <div className="edit-footer">
            <button onClick={handleSave} className="btn btn-primary" disabled={!isValidConfig}>
              Save
            </button>
            <button onClick={() => setIsEditing(false)} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="language-detection-node"
      onDoubleClick={() => setIsEditing(true)}
      style={{
        borderColor: hasMissingVariants ? '#d97706' : 'inherit'
      }}
    >
      <div className="node-header">
        <Globe className="w-4 h-4" />
        <span>Language Detection</span>
      </div>
      <div className="node-status">{statusBadge}</div>
      {isValidConfig && (
        <>
          <div className="node-detail">
            <strong>Method:</strong> {detectionMethod}
          </div>
          <div className="node-detail">
            <strong>Languages:</strong> {languages.join(', ')} ({languages.length})
          </div>
          <div className="node-detail">
            <strong>Codes:</strong> <code style={{ fontSize: '11px', backgroundColor: '#f3f4f6', padding: '2px 4px', borderRadius: '3px' }}>
              {languages.join(', ')}
            </code>
          </div>
          <div className="node-detail">
            <strong>Prompt:</strong> <em>{customMessage.length > 40 ? customMessage.slice(0, 37) + '...' : customMessage}</em>
          </div>
          {hasMissingVariants && (
            <div style={{ padding: '6px', backgroundColor: '#fef3c7', borderRadius: '3px', marginTop: '6px' }}>
              <small style={{ color: '#92400e' }}>
                ⚠️ Missing variants: {missingLanguageVariants.join(', ')}
              </small>
            </div>
          )}
        </>
      )}
      <div className="node-hint">Double-click to edit</div>
    </div>
  );
}
